import React from 'react';

/**
 * MetricsCard - Reusable component for displaying key metrics
 * Used across Super Admin, School Admin, and Teacher dashboards
 *
 * @param {string} title - Card title
 * @param {string|number} value - Main metric value
 * @param {string} subtitle - Optional subtitle or description
 * @param {string} trend - Optional trend indicator (e.g., "▲ 12%", "▼ 5%")
 * @param {string} trendColor - Color for trend (green, red, gray)
 * @param {React.ReactNode} icon - Optional icon component or emoji string
 * @param {function} onClick - Optional click handler
 */
const MetricsCard = ({
  title,
  value,
  subtitle,
  trend,
  trendColor = 'green',
  icon,
  onClick,
  valueClassName
}) => {
  const getTrendColorClass = () => {
    switch(trendColor) {
      case 'green':
        return 'text-green-600';
      case 'red':
        return 'text-red-600';
      case 'yellow':
        return 'text-yellow-600';
      case 'gray':
        return 'text-gray-500';
      default:
        return 'text-gray-600';
    }
  };

  const isJSXIcon = typeof icon !== 'string' && icon != null;

  const cardClasses = `
    bg-white rounded-lg p-6 shadow-sm border border-gray-200 border-l-4 border-l-indigo-200
    hover:shadow-md transition-shadow
    ${onClick ? 'cursor-pointer' : ''}
  `;

  return (
    <div className={cardClasses} onClick={onClick}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-medium text-gray-600">{title}</p>
        {icon && (
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-50 to-purple-50 flex items-center justify-center text-lg text-indigo-600">
            {isJSXIcon ? icon : icon}
          </div>
        )}
      </div>

      <div className="flex items-baseline space-x-2">
        <h3 className={`font-bold text-gray-900 ${valueClassName || 'text-3xl'}`}>{value}</h3>
        {trend && (
          <span className={`text-sm font-semibold ${getTrendColorClass()}`}>
            {trend}
          </span>
        )}
      </div>

      {subtitle && (
        <p className="mt-1 text-xs text-gray-500">{subtitle}</p>
      )}
    </div>
  );
};

export default MetricsCard;
